import PropTypes from 'prop-types';
import { jsonLdScriptProps } from 'react-schemaorg';
import Head from 'next/head';
import { useEffect, useState } from 'react';
import { StoryblokCache, initializeRedis } from '@cache/redis';
import { OllieStoryblokClient } from '@web/cms/api';
import { fetchVanityUrl } from '@web/cms/fetchVanityUrl';
import { logger } from '@web/logger';
import { withGladlySetting } from '../gladly';
import { withFlagshipSession } from '../auth/withFlagshipSession';
import { Bridge, CMSMeta, CMSStory, useCMSEditor, VALID_STORIES } from '../cms';
import { server, shared } from '../config';
import { PrefixedReporterProvider, useTrackPage } from '../reporter';

const CMSPage = ({ content, bridged, isHomePage, path }) => {
  const syncedContent = useCMSEditor(content);
  const [hasHydrated, setHasHydrated] = useState(false);
  const taggingPrefix = syncedContent?.tagging_prefix ?? 'CMS Page';
  useTrackPage(taggingPrefix);
  const META_SITE_URL = `https://${shared.DOMAIN_PUBLIC_NAME}`;

  const siteUrl = `https://www.${shared.SITE_NAME}`;
  const cxEmail = `caninecare@${shared.SITE_NAME}`;

  const pageUrl = `${META_SITE_URL}/${path || ''}`;
  const pageName = content.seo_title || content.name;
  const pageImage = content.social_image;
  const pageDesc = content.product_description;
  const faqs = content.faqs ?? [];
  const itemsList = content.items_list ?? [];
  const aggregateRating = content.aggregate_rating?.[0] ?? null;

  useEffect(() => {
    setHasHydrated(true);
  }, []);

  return (
    <>
      <Head>
        {isHomePage && (
          <script
            {...jsonLdScriptProps({
              '@context': 'https://schema.org',
              '@type': 'Organization',
              name: 'Ollie',
              url: siteUrl,
              logo: '/ollie-logo.svg',
              description:
                'Ollie produces human-grade, clean dog food with real ingredients dogs love to eat, tailoring it to their individual nutritional needs and delivering it to homes.',
              contactPoint: {
                '@type': 'ContactPoint',
                contactType: 'Customer Care',
                telephone: '844-886-5543',
                email: cxEmail,
                areaServed: 'US',
                availableLanguage: 'English',
              },
              sameAs: [
                'https://twitter.com/olliepets',
                'https://www.facebook.com/MyOllie',
                'https://www.instagram.com/myollie/',
                'https://www.pinterest.com/myolliedotcom/',
              ],
            })}
          />
        )}
        <script
          type="text/javascript"
          src={`${shared.AWS_WAF_CHALLENGE_INTEGRATION_URL}`}
          defer
        />
        {hasHydrated && itemsList?.length > 0 && (
          <script
            {...jsonLdScriptProps({
              '@context': 'https://schema.org',
              '@type': 'ItemList',
              name: pageName,
              url: pageUrl,
              numberOfItems: itemsList.length,
              itemListElement: itemsList.map((item, index) => ({
                '@type': 'ListItem',
                position: index + 1,
                name: item.name,
                url: item.url.url,
              })),
            })}
          />
        )}

        {hasHydrated && faqs?.length > 0 && (
          <script
            {...jsonLdScriptProps({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: faqs.map(faq => ({
                '@type': 'Question',
                name: faq.question,
                acceptedAnswer: {
                  '@type': 'Answer',
                  text: faq.answer,
                },
              })),
            })}
          />
        )}
        {hasHydrated && aggregateRating && (
          <script
            {...jsonLdScriptProps({
              '@context': 'https://schema.org',
              '@type': 'Product',
              name: pageName,
              url: pageUrl,
              image: pageImage,
              description: pageDesc,
              brand: {
                '@type': 'Brand',
                name: 'Ollie',
              },
              aggregateRating: {
                '@type': 'AggregateRating',
                ratingValue: aggregateRating.rating_value,
                reviewCount: aggregateRating.rating_count,
              },
            })}
          />
        )}
      </Head>
      {bridged && <Bridge />}
      <CMSMeta content={syncedContent} />
      <PrefixedReporterProvider prefix={taggingPrefix}>
        <CMSStory content={syncedContent} isHomePage={isHomePage} />
      </PrefixedReporterProvider>
    </>
  );
};

/**
 * @type {import('next').GetServerSideProps}
 */
export const getServerSideProps = withGladlySetting(
  withFlagshipSession(async ({ params, preview }) => {
    const requestPath = params.path;
    // If someone requests the HOME_SLUG directly, send them to `/`.
    if (requestPath?.[0] === OllieStoryblokClient.HOME_SLUG) {
      return {
        redirect: {
          destination: '/',
          permanent: true,
        },
      };
    }

    const redis = initializeRedis({
      host: server.REDIS_HOST,
      port: server.REDIS_PORT,
    });
    const storyblokRedis = new StoryblokCache(redis);
    const ollieStoryblokClient = new OllieStoryblokClient(storyblokRedis);

    const path = requestPath?.join('/') ?? OllieStoryblokClient.HOME_SLUG;

    // Find if the requestPath matches any of the vanity URLs
    const foundVanityUrl = await fetchVanityUrl(ollieStoryblokClient, path);

    if (foundVanityUrl) {
      return {
        redirect: {
          destination: foundVanityUrl.target_url,
          permanent: false, // Set to `true` if it's a permanent redirect (301)
        },
      };
    }

    const isHomePage = path === OllieStoryblokClient.HOME_SLUG;

    try {
      const res = await ollieStoryblokClient.getStory(path);
      const { content } = res.data.story;
      // If someone is on an offer_redirect page,
      if (content.component === 'offer_redirect') {
        const targetUrl = content.target.cached_url || content.target.url;
        const redirectTarget =
          targetUrl === OllieStoryblokClient.HOME_SLUG
            ? '/'
            : '/' + targetUrl + '/';
        return {
          redirect: {
            destination: `${redirectTarget}?offer_code=${content.coupon_code}`,
            permanent: true,
          },
        };
      }

      if (!VALID_STORIES.includes(content.component)) {
        return {
          notFound: true,
        };
      }

      return {
        props: {
          content,
          bridged: Boolean(preview),
          isHomePage,
          path,
        },
      };
    } catch (e) {
      if (e.response?.status === 404) {
        return {
          notFound: true,
        };
      }
      logger.error(
        { err: e },
        `Error fetching data for page ${path}: ${e.message}`,
      );

      throw e;
    }
  }),
);

export default CMSPage;

CMSPage.propTypes = {
  content: PropTypes.object,
  bridged: PropTypes.bool,
  isHomePage: PropTypes.bool,
  path: PropTypes.string,
};
